import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Download } from "lucide-react";
import { getStormRanges } from "@/lib/stormsQueries";
import { supabase } from "@/lib/supabaseClient";

// ─── Chart layout constants ──────────────────────────────────────────────────
const ROW_HEIGHT         = 38;  // px allocated per season bar
const AXIS_HEIGHT        = 50;  // px reserved for x-axis labels
const MIN_CHART_HEIGHT   = 80;
const DOMAIN_MAX         = 365; // Sep 1 = day 0 … Aug 31 ≈ day 364
const MIN_BAR_DAYS       = 3;   // minimum segment width (days) for visual clarity
const PNG_CREDIT_HEIGHT  = 24;  // extra canvas height for the "UKWX_" credit line

// X-axis month tick positions (day offset from Sep 1 of the season year)
const SEASON_MONTH_TICKS = [
  { day: 0,   label: "Sep" },
  { day: 30,  label: "Oct" },
  { day: 61,  label: "Nov" },
  { day: 91,  label: "Dec" },
  { day: 122, label: "Jan" },
  { day: 153, label: "Feb" },
  { day: 181, label: "Mar" },
  { day: 212, label: "Apr" },
  { day: 242, label: "May" },
  { day: 273, label: "Jun" },
  { day: 303, label: "Jul" },
  { day: 334, label: "Aug" },
];

// Reduced tick set for narrow/mobile screens (every ~2 months)
const SEASON_MONTH_TICKS_MOBILE = [
  { day: 0,   label: "Sep" },
  { day: 61,  label: "Nov" },
  { day: 122, label: "Jan" },
  { day: 181, label: "Mar" },
  { day: 242, label: "May" },
  { day: 303, label: "Jul" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Convert a "YYYY-MM-DD" date string to a day offset from Sep 1 of the
 * storm season that date belongs to (Sep 1 = 0).
 * @param {string} dateStr
 * @returns {number}
 */
function getSeasonDayOffset(dateStr) {
  const [yearStr, monthStr, dayStr] = dateStr.split("-");
  const year  = parseInt(yearStr,  10);
  const month = parseInt(monthStr, 10); // 1-based
  const day   = parseInt(dayStr,   10);

  // For Sep–Dec the season year equals the calendar year;
  // for Jan–Aug the season year is the previous calendar year.
  const seasonYear  = month >= 9 ? year : year - 1;
  const date        = new Date(year, month - 1, day);
  const seasonStart = new Date(seasonYear, 8, 1); // Sep 1 (month 0-based)

  return Math.round((date - seasonStart) / (1000 * 60 * 60 * 24));
}

/**
 * Generate n evenly-spaced HSL colours across the full hue wheel.
 * Supports 20+ storms with no hardcoded palette.
 * @param {number} n
 * @returns {string[]}
 */
function generateColors(n) {
  if (n === 0) return [];
  return Array.from({ length: n }, (_, i) =>
    `hsl(${Math.round((i / n) * 360)}, 45%, 72%)`
  );
}

/**
 * Group storm ranges by season, sort by start_date, assign cumulative numbers,
 * and build the Recharts row data for a horizontal stacked bar chart.
 *
 * Each season row uses alternating spacer_N / storm_N keys so that each storm
 * block sits at its correct seasonal-day x-position.
 *
 * @param {Array<{id: string, storm_name: string, season: string, start_date: string, end_date: string}>} stormRanges
 * @returns {{ rows: object[], maxStorms: number, colors: string[] }}
 */
function buildChartData(stormRanges) {
  /** @type {Object.<string, Array>} */
  const seasonMap = {};
  for (const storm of stormRanges) {
    if (!seasonMap[storm.season]) seasonMap[storm.season] = [];
    seasonMap[storm.season].push(storm);
  }

  // Seasons newest first
  const sortedSeasons = Object.keys(seasonMap).sort((a, b) => b.localeCompare(a));

  const seasonData = sortedSeasons.map((season) => {
    const sorted = [...seasonMap[season]].sort(
      (a, b) => a.start_date.localeCompare(b.start_date)
    );
    return {
      season,
      storms: sorted.map((s, i) => ({
        startDay: getSeasonDayOffset(s.start_date),
        endDay:   getSeasonDayOffset(s.end_date),
        cumulativeNum: i + 1,
      })),
    };
  });

  const maxStorms = Math.max(...seasonData.map((s) => s.storms.length), 0);
  const colors    = generateColors(maxStorms);

  const rows = seasonData.map(({ season, storms }) => {
    /** @type {Object.<string, number>} */
    const row = { season };
    // currentDay tracks the next available start position on the x-axis
    let currentDay = 0;

    storms.forEach((storm, idx) => {
      const n = idx + 1;
      // Enforce a minimum width for visual clarity
      const duration = Math.max(storm.endDay - storm.startDay + 1, MIN_BAR_DAYS);
      // Advance to the storm's start day, but never go backwards
      const effectiveStart = Math.max(currentDay, storm.startDay);

      row[`spacer_${n}`] = Math.max(0, effectiveStart - currentDay);
      row[`storm_${n}`]  = duration;
      row[`label_${n}`]  = storm.cumulativeNum;
      currentDay = effectiveStart + duration;
    });

    // Pad unused storm slots so every row shares the same shape
    for (let n = storms.length + 1; n <= maxStorms; n++) {
      row[`spacer_${n}`] = 0;
      row[`storm_${n}`]  = 0;
      row[`label_${n}`]  = 0;
    }

    return row;
  });

  return { rows, maxStorms, colors };
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Standalone reusable function that fetches the latest storm ranges data.
 * Can be called from outside the component (e.g. after inserting a new storm)
 * to prime a cache or trigger an external refresh key.
 * @returns {Promise<Array>}
 */
export async function fetchStormAccumulationData() {
  return getStormRanges();
}

export default function StormAccumulationChart({ refreshKey } = {}) {
  const [stormRanges, setStormRanges] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const chartContainerRef             = useRef(null);

  /**
   * Reusable refresh function – fetch the latest storm ranges and update state.
   * Called on mount, by the realtime subscription, and when `refreshKey` changes.
   */
  const refreshChart = useCallback(() => {
    setLoading(true);
    getStormRanges()
      .then(setStormRanges)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Initial fetch
  useEffect(() => {
    refreshChart();
  }, [refreshChart]);

  // Re-fetch whenever the caller increments refreshKey (external trigger)
  useEffect(() => {
    if (refreshKey === null || refreshKey === undefined) return;
    refreshChart();
  }, [refreshKey, refreshChart]);

  // Supabase realtime: auto-refresh when named_storms table changes
  useEffect(() => {
    const channel = supabase
      .channel("storm-accumulation-named-storms")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "named_storms" },
        () => { refreshChart(); }
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [refreshChart]);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    observer.observe(chartContainerRef.current);
    return () => observer.disconnect();
  }, []);

  const { rows, maxStorms, colors } = useMemo(() => {
    if (!stormRanges.length) return { rows: [], maxStorms: 0, colors: [] };
    return buildChartData(stormRanges);
  }, [stormRanges]);

  /**
   * Export the chart SVG as a PNG with a white background and "UKWX_" credit.
   */
  const exportToPng = useCallback(() => {
    const container = chartContainerRef.current;
    if (!container) return;
    const svg = container.querySelector("svg");
    if (!svg) return;

    const rect   = svg.getBoundingClientRect();
    const width  = Math.round(rect.width);
    const height = Math.round(rect.height);
    const creditHeight = PNG_CREDIT_HEIGHT;

    // Clone the SVG and insert a white background rect
    const svgClone = svg.cloneNode(true);
    svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    svgClone.setAttribute("width",  String(width));
    svgClone.setAttribute("height", String(height));

    const bgRect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    bgRect.setAttribute("x",      "0");
    bgRect.setAttribute("y",      "0");
    bgRect.setAttribute("width",  String(width));
    bgRect.setAttribute("height", String(height));
    bgRect.setAttribute("fill",   "white");
    svgClone.insertBefore(bgRect, svgClone.firstChild);

    const svgStr  = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl  = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onerror = () => URL.revokeObjectURL(svgUrl);
    img.onload  = () => {
      const canvas     = document.createElement("canvas");
      canvas.width     = width;
      canvas.height    = height + creditHeight;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, width, height);

      // Credit text – bottom-right corner
      ctx.fillStyle  = "#6B7280";
      ctx.font       = "11px sans-serif";
      ctx.textAlign  = "right";
      ctx.fillText("UKWX_", canvas.width - 8, canvas.height - 7);

      canvas.toBlob((blob) => {
        if (!blob) return;
        const a    = document.createElement("a");
        a.href     = URL.createObjectURL(blob);
        a.download = "storm-accumulation.png";
        a.click();
      });

      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="h-48 bg-[#111827] border border-white/10 animate-pulse" />;
  }

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 text-red-400 text-sm">
        Failed to load accumulation data: {error}
      </div>
    );
  }

  if (!rows.length) return null;

  const chartHeight = Math.max(rows.length * ROW_HEIGHT + AXIS_HEIGHT, MIN_CHART_HEIGHT);
  const isMobile    = containerWidth > 0 && containerWidth < 480;
  const activeTicks = isMobile ? SEASON_MONTH_TICKS_MOBILE : SEASON_MONTH_TICKS;
  const tickValues  = activeTicks.map((t) => t.day);
  const tickLabels  = Object.fromEntries(activeTicks.map((t) => [t.day, t.label]));
  const yAxisWidth  = isMobile ? 48 : 62;
  const tickFontSize = isMobile ? 9 : 11;
  const yFontSize    = isMobile ? 10 : 12;

  return (
    <div className="border border-white/10 bg-[#111827] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#F9FAFB]">
            Named Storm Accumulation by Season
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Continuous storm ranges positioned by date within season
          </p>
        </div>
        <button
          onClick={exportToPng}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-[#9CA3AF] border border-white/10 hover:border-white/20 hover:text-[#F9FAFB] transition-colors"
          title="Export as PNG"
        >
          <Download className="w-3 h-3" />
          PNG
        </button>
      </div>

      {/* Chart */}
      <div className="px-2 py-3" ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={rows}
            margin={{ top: 2, right: isMobile ? 8 : 20, left: 0, bottom: 28 }}
            barCategoryGap="30%"
          >
            <XAxis
              type="number"
              domain={[0, DOMAIN_MAX]}
              ticks={tickValues}
              tickFormatter={(v) => tickLabels[v] ?? ""}
              tick={{ fill: "#6B7280", fontSize: tickFontSize }}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="season"
              width={yAxisWidth}
              tick={{ fill: "#9CA3AF", fontSize: yFontSize }}
              axisLine={false}
              tickLine={false}
            />
            {Array.from({ length: maxStorms }).flatMap((_, i) => {
              const n = i + 1;
              return [
                <Bar
                  key={`spacer_${n}`}
                  dataKey={`spacer_${n}`}
                  stackId="a"
                  fill="transparent"
                  stroke="none"
                  isAnimationActive={false}
                />,
                <Bar
                  key={`storm_${n}`}
                  dataKey={`storm_${n}`}
                  stackId="a"
                  fill={colors[i]}
                  stroke="rgba(0,0,0,0.15)"
                  strokeWidth={0.5}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey={`label_${n}`}
                    position="inside"
                    formatter={(v) => (v > 0 ? String(v) : "")}
                    style={{ fill: "#0f172a", fontSize: 10, fontWeight: "bold" }}
                  />
                </Bar>,
              ];
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
